import { useEffect, useMemo, useRef, useState } from 'react';
import { mapFaceBoxToViewportSquare, type CameraSize } from '../../features/camera/cameraGeometry';
import type { ColorSample, GeoState, TrackedFace } from '../../types';

interface CameraHudProps {
  faces: TrackedFace[];
  faceStatus: 'loading' | 'ready' | 'offline';
  location: GeoState;
  palette: ColorSample[];
  showFace: boolean;
  showGps: boolean;
  showPalette: boolean;
  sourceSize?: CameraSize | null;
  mirrored?: boolean;
}

const gpsText = (location: GeoState) => location.status === 'ready' && location.latitude != null
  ? `${location.latitude.toFixed(4)}°  ${location.longitude?.toFixed(4)}°`
  : location.status === 'loading' ? '定位中' : '未取得';

const EMPTY_SIZE: CameraSize = { width: 0, height: 0 };

export function CameraHud({ faces, faceStatus, location, palette, showFace, showGps, showPalette, sourceSize, mirrored = false }: CameraHudProps) {
  const hudRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState<CameraSize>(EMPTY_SIZE);

  useEffect(() => {
    const element = hudRef.current;
    if (!element) return;
    const update = () => {
      const next = { width: element.clientWidth, height: element.clientHeight };
      setViewportSize((current) => current.width === next.width && current.height === next.height ? current : next);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const now = new Date();
  const detectedFaces = useMemo(() => {
    if (!showFace || !sourceSize || viewportSize.width <= 0 || viewportSize.height <= 0) return [];
    return faces.flatMap((face) => {
      // Keep geometry and the CSS minimum in lockstep so a small edge detection
      // remains a true 1:1 box without overflowing after min-size is applied.
      const box = mapFaceBoxToViewportSquare(face.bbox, sourceSize, viewportSize, mirrored, 40);
      return box ? [{ face, box }] : [];
    });
  }, [faces, mirrored, showFace, sourceSize, viewportSize]);
  const scanning = showFace && faceStatus !== 'offline' && detectedFaces.length === 0;

  return <div ref={hudRef} className="camera-hud" aria-hidden="true">
    <div className="hud-grid"/>{scanning ? <div className="hud-scan-line"/> : null}<div className="hud-focus-orbit"><i/><i/></div>
    <div className="hud-side-scale left"/><div className="hud-side-scale right"/>
    <div className="hud-frame"><i className="corner tl"/><i className="corner tr"/><i className="corner bl"/><i className="corner br"/><span className="crosshair">＋</span></div>
    {detectedFaces.map(({ face, box }) => <div key={face.id} className="face-box" style={{ left: box.x, top: box.y, width: box.width, height: box.height }}><span>{face.label}</span></div>)}
    <div className="camera-meta mono">
      <div><span>日期</span>{now.toLocaleDateString('zh-TW')}</div>
      <div><span>時間</span>{now.toLocaleTimeString('zh-TW', { hour12: false })}</div>
      {showGps ? <div><span>GPS</span>{gpsText(location)}</div> : null}
      <div><span>FACE</span>{faceStatus === 'ready' ? detectedFaces.length ? `${detectedFaces.length} TRACK` : '掃描中' : faceStatus === 'loading' ? '載入中' : '追蹤未啟用'}</div>
    </div>
    {showPalette ? <div className="live-palette"><span>LIVE PALETTE</span><div>{palette.map((color) => <i key={color.hex} style={{ background: color.hex }} />)}{palette.length === 0 ? Array.from({ length: 5 }, (_, index) => <i key={index} />) : null}</div></div> : null}
  </div>;
}
